/**
 * @created     2026-03-18
 * @description Renders structured widget cards for bot responses.
 *              Supports claim_overview, payment_terms, bank_details,
 *              payment_history, and contact widget types.
 *              Depends on: BotWidget model
 */
import { Component, input, output, ChangeDetectionStrategy, inject, effect, signal } from '@angular/core';
import QRCode from 'qrcode';
import { BotWidget, ChatAction } from '../../../core/models/bot-response.model';
import { I18nService } from '../../../core/services/i18n.service';
import { TranslatePipe } from '../../pipes/translate.pipe';

/** Shared EPC encoding for chat cards and the transfer wizard. */
export async function createTransferQr(
  bankData: Record<string, unknown> | null,
  formatCurrency: (amount: number) => string,
): Promise<string | null> {
  const value = bankData?.['epc'];
  if (!bankData || !value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const epc = value as Record<string, unknown>;
  const text = (data: Record<string, unknown>, keys: string[]): string => {
    for (const key of keys) {
      const value = data[key];
      if (typeof value === 'string' && value.trim() !== '') {
        return value.trim() === '-' ? '' : value.trim();
      }
      if (typeof value === 'number' && Number.isFinite(value)) {
        return formatCurrency(value);
      }
    }
    return '';
  };
  const recipient = text(epc, ['empfaenger', 'recipient', 'name'])
    || text(bankData, ['empfaenger', 'recipient', 'name']);
  const iban = text(epc, ['iban', 'bank_iban']) || text(bankData, ['iban', 'bank_iban']);
  if (!recipient || !iban) {
    return null;
  }
  const amount = text(epc, ['amount']);
  const reference = text(epc, ['verwendungszweck', 'reference', 'purpose'])
    || text(bankData, ['verwendungszweck', 'reference', 'purpose']);
  const payload = [
    'BCD', text(epc, ['version']) || '002', '1', 'SCT', text(epc, ['bic', 'bank_bic']),
    recipient.slice(0, 70), iban.replace(/\s/g, ''), amount ? `EUR${amount}` : '',
    '', '', reference.slice(0, 140), '',
  ].join('\n');
  return QRCode.toDataURL(payload, {
    errorCorrectionLevel: 'M', margin: 2, width: 210,
    color: { dark: '#1f2933', light: '#ffffff' },
  });
}

@Component({
  selector: 'app-widget-card',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './widget-card.component.html',
  styleUrl: './widget-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetCardComponent {
  private readonly i18n = inject(I18nService);

  readonly widgets = input.required<BotWidget[]>();
  readonly actions = input<ChatAction[]>([]);
  readonly disabled = input(false);
  readonly actionClick = output<ChatAction>();
  readonly claimDetailsAvailable = input(false);
  readonly claimDetails = output<HTMLElement>();

  readonly transferQrCode = signal<string | null>(null);

  constructor() {
    effect((onCleanup) => {
      const bankData = this.widgets().find(widget => widget.type === 'bank_details')?.data ?? null;
      this.transferQrCode.set(null);
      let current = true;
      onCleanup(() => { current = false; });
      void createTransferQr(bankData, amount => this.i18n.formatCurrency(amount))
        .then(qr => { if (current) this.transferQrCode.set(qr); })
        .catch(() => { if (current) this.transferQrCode.set(null); });
    });
  }

  actionFor(payload: string): ChatAction | undefined {
    return this.actions().find(action => action.payload === payload);
  }

  hasClaimDetails(data: Record<string, unknown>): boolean {
    return ['invoices', 'titles', 'payments'].some(key => Array.isArray(data[key]) && (data[key] as unknown[]).length > 0);
  }

  /** Copy IBAN to clipboard */
  copyIban(iban: string): void {
    navigator.clipboard.writeText(iban.replace(/\s/g, '')).catch(() => {
      // Fallback: do nothing on clipboard failure
    });
  }

  formatDate(value: unknown): unknown {
    return typeof value === 'string' ? this.i18n.formatDate(value) : value;
  }

  hasDuplicateLastRate(rateInfo: Record<string, any>): boolean {
    const last = rateInfo['letzteRate'];
    // Hide the last row only when both date and amount match an earlier displayed rate.
    return !!last && ['aktuelleRate', 'naechsteRate', 'uebernaechsteRate', 'vorletzteRate']
      .some(key => rateInfo[key] && rateInfo[key].datum === last.datum && rateInfo[key].betrag === last.betrag);
  }

  /** Helper to get widget title */
  getTitle(type: string, data: Record<string, unknown> = {}): string {
    const titles: Record<string, string> = {
      claim_overview: this.i18n.t('widget.titles.claim_overview'),
      payment_terms: this.i18n.t('widget.titles.payment_terms'),
      bank_details: this.i18n.t('widget.titles.bank_details'),
      payment_history: this.i18n.t('widget.titles.payment_history'),
      payment_agreement: this.i18n.t('widget.titles.payment_agreement'),
      creditreform_contact: this.i18n.t('widget.titles.creditreform_contact'),
    };

    if (type === 'payment_history' && data['rateninformationen']) {
      return this.i18n.t('widget.titles.payment_agreement');
    }

    return titles[type] ?? type;
  }

}
